import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FiCheck, FiX, FiPlay, FiAlertTriangle, FiZap, FiChevronLeft, FiChevronRight, FiLoader } from 'react-icons/fi';
import AppModal from '../common/AppModal';
import { formatApiError } from '../../utils/formatters';
import { IS_STANDALONE } from '../../config/features';
import './CollectionWizard.css';

/**
 * 새 수집 마법사 (2026-10-03) — **화면이 절차를 쥔다.** 대상 → 사전 점검 → 묶음 → (맞춤 수집) → 대시보드 → 확인·시작 → (공유).
 * 실행(등록·시작·대시보드 만들기)은 마지막 단계에서 사용자가 누를 때만 하고, 서버가 단계마다 다시 확인한다
 * (등록 가능한 연결인지, SQL 이 SELECT 한 문장인지, 남의 기준이 아닌지 — backend collectionAccess · monitoringRouter wizard/*).
 * AI 보조(aiAssist)는 칸을 **채우기만** 한다 — 등록·시작 도구를 갖지 않는다. AI 가 없으면 그 단추만 감춘다.
 */
// 조회 공유는 웹의 것 — 스탠드얼론은 혼자라 마지막 단계가 없다(config/features.js IS_STANDALONE)
const STEPS = IS_STANDALONE
  ? ['target', 'precheck', 'groups', 'custom', 'dashboard', 'confirm']
  : ['target', 'precheck', 'groups', 'custom', 'dashboard', 'confirm', 'share'];
// Oracle 의 기본 묶음 — 사전 점검 전의 기본값. 점검 결과(pre.groups)가 오면 **그 DBMS 의 묶음**으로 바뀐다(수집 프로파일)
const BUILTIN_GROUPS = ['sessions', 'sysstat', 'wait', 'memory'];
const INTERVALS = [10, 30, 60, 300];
const RETAINS = [1, 3, 7, 14];

export default function CollectionWizard({ open, onClose, apiBase, onDone, aiAssist }) {
  const { t } = useTranslation();
  const mon = `${apiBase}/monitoring`;

  const [step, setStep] = useState(0);
  const [instances, setInstances] = useState([]);
  const [target, setTarget] = useState(null);           // { instanceId, connId, connName, registeredByMe }
  const [pre, setPre] = useState(null);                 // precheck 결과
  const [preBusy, setPreBusy] = useState(false);
  const [preError, setPreError] = useState(null);
  const [groups, setGroups] = useState(['sessions', 'sysstat', 'wait', 'memory']);
  const [intervalSec, setIntervalSec] = useState(30);
  const [retainDays, setRetainDays] = useState(7);
  const [customOn, setCustomOn] = useState(false);
  const [custom, setCustom] = useState({ name: '', sql: '' });
  const [test, setTest] = useState(null);               // { columns, rows, elapsedMs } | { error }
  const [testBusy, setTestBusy] = useState(false);
  const [makeDash, setMakeDash] = useState(false);
  const [dashName, setDashName] = useState('');
  const [runLog, setRunLog] = useState([]);             // [{ text, ok }]
  const [running, setRunning] = useState(false);
  const [doneOk, setDoneOk] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);
  const [aiNote, setAiNote] = useState({});             // 단계별 AI 설명
  const [goal, setGoal] = useState('');
  // 공유
  const [viewers, setViewers] = useState([]);
  const [q, setQ] = useState('');
  const [found, setFound] = useState([]);
  const timer = useRef(null);

  // 열릴 때마다 처음부터
  useEffect(() => {
    if (!open) return;
    setStep(0); setTarget(null); setPre(null); setPreError(null); setGroups(['sessions', 'sysstat', 'wait', 'memory']);
    setIntervalSec(30); setRetainDays(7); setCustomOn(false); setCustom({ name: '', sql: '' }); setTest(null);
    setMakeDash(false); setDashName(''); setRunLog([]); setRunning(false); setDoneOk(false); setAiNote({}); setGoal('');
    setViewers([]); setQ(''); setFound([]);
    axios.get(`${mon}/instances`).then(r => setInstances(r.data.data || [])).catch(() => setInstances([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groupLabel = (g) => t(`monitoring:wiz.group.${g}`, { defaultValue: g });
  // 이 대상의 DBMS 에서 고를 수 있는 내장 묶음
  const builtinGroups = (pre && Array.isArray(pre.groups) && pre.groups.length > 0) ? pre.groups : BUILTIN_GROUPS;
  const dbType = (pre && pre.dbType) || 'oracle';

  // ── 단계별 「다음」 가능 여부 ──
  const failedGroups = useMemo(() => new Set((pre?.checks || []).filter(c => !c.ok).flatMap(c => c.groups || [])), [pre]);
  const testOk = !!(test && test.columns && test.columns.some(c => c.role === 'value' || c.role === 'counter'));
  const canNext = (() => {
    switch (STEPS[step]) {
      case 'target': return !!target;
      case 'precheck': return !!pre && !pre.alreadyOwned && !preBusy;
      case 'groups': return groups.some(g => !failedGroups.has(g)) || customOn;
      case 'custom': return !customOn || (custom.name.trim() && testOk);
      case 'dashboard': return !makeDash || !!dashName.trim();
      default: return true;
    }
  })();

  // ── 사전 점검 ──
  const runPrecheck = async (tg) => {
    setPreBusy(true); setPreError(null); setPre(null);
    try {
      const r = await axios.post(`${mon}/wizard/precheck`, { connectionId: tg.connId });
      setPre(r.data);
      const offered = Array.isArray(r.data.groups) && r.data.groups.length > 0 ? r.data.groups : BUILTIN_GROUPS;
      setGroups(offered.filter(g => !(r.data.checks || []).some(c => !c.ok && (c.groups || []).includes(g))));
    } catch (err) { setPreError(formatApiError(err, t)); } finally { setPreBusy(false); }
  };
  const go = (d) => {
    const next = Math.min(Math.max(step + d, 0), STEPS.length - 1);
    if (STEPS[next] === 'precheck' && target && (!pre || pre.instanceId !== target.instanceId)) runPrecheck(target);
    setStep(next);
  };

  // ── 맞춤 수집 시험 ──
  const runTest = async () => {
    setTestBusy(true); setTest(null);
    try {
      const r = await axios.post(`${mon}/wizard/test-sql`, { connectionId: target.connId, sql: custom.sql });
      setTest(r.data);
    } catch (err) { setTest({ error: formatApiError(err, t) }); } finally { setTestBusy(false); }
  };
  const setRole = (name, role) => setTest(tt => ({ ...tt, columns: tt.columns.map(c => (c.name === name ? { ...c, role } : c)) }));

  // ── AI 보조 — 칸을 채우기만 한다 ──
  const askAi = async (kind) => {
    if (!aiAssist) return;
    setAiBusy(kind);
    setAiNote(n => ({ ...n, [kind]: t('monitoring:wiz.ai.thinking') }));
    try {
      const r = await aiAssist(kind, { goal, precheck: pre, sql: custom.sql, test: test && test.error ? { error: test.error } : null });
      if (kind === 'groups' && r) {
        if (Array.isArray(r.groups)) setGroups(r.groups.filter(g => builtinGroups.includes(g) && !failedGroups.has(g)));
        if (INTERVALS.includes(r.intervalSec)) setIntervalSec(r.intervalSec);
        if (r.custom && r.custom.sql) { setCustomOn(true); setCustom({ name: r.custom.name || '', sql: r.custom.sql }); setTest(null); }
      }
      if (kind === 'sql' && r && r.sql) { setCustom(c => ({ name: c.name || r.name || '', sql: r.sql })); setTest(null); }
      setAiNote(n => ({ ...n, [kind]: (r && r.explanation) || '' }));
    } catch (err) {
      setAiNote(n => ({ ...n, [kind]: formatApiError(err, t) }));
    } finally { setAiBusy(null); }
  };

  // ── 시작 — 사용자가 누를 때만. 서버가 단계마다 다시 확인한다 ──
  const start = async () => {
    setRunning(true); setRunLog([]);
    const log = (text, ok = true) => setRunLog(l => [...l, { text, ok }]);
    const inst = target.instanceId;
    const enc = encodeURIComponent;
    let anyOk = false;
    const todo = groups.filter(g => !failedGroups.has(g));
    try {
      if (customOn) {
        const r = await axios.post(`${mon}/wizard/custom-queries`, { queryName: custom.name, selectSql: custom.sql, columns: test.columns });
        todo.push(r.data.data.query_id);
        log(t('monitoring:wiz.run.customSaved', { name: custom.name }));
      }
      for (const g of todo) {
        try {
          const body = { connectionId: target.connId, enabled: true, intervalSec, retainDays, maxMb: 500 };
          await axios.put(`${mon}/instances/${enc(inst)}/groups/${enc(g)}`, body);
          await axios.post(`${mon}/instances/${enc(inst)}/groups/${enc(g)}/start`, { connectionId: target.connId });
          log(t('monitoring:wiz.run.started', { name: builtinGroups.includes(g) ? groupLabel(g) : custom.name }));
          anyOk = true;
        } catch (err) { log(`${builtinGroups.includes(g) ? groupLabel(g) : custom.name}: ${formatApiError(err, t)}`, false); }
      }
      if (makeDash && anyOk) {
        try {
          const panelsRes = await axios.get(`${mon}/panels`);
          const tabs = (panelsRes.data.data && panelsRes.data.data.tabs) || [];
          // 이 DBMS 의 내장 탭에서 — 탭은 수집 묶음 id(groupId)와 DBMS(dbType, 없으면 Oracle)를 갖는다
          const picked = tabs.filter(tb => tb.kind === 'builtin' && (tb.dbType || 'oracle') === dbType && todo.includes(tb.groupId || tb.id)).flatMap(tb => tb.panels || []);
          // 대상을 고정해 둔다 — 다른 수집이 생겨도 이 대시보드는 이 DB 만 본다
          const created = await axios.post(`${mon}/panels/tabs`, { name: dashName.trim(), targetInstanceId: inst });
          const tabId = created.data.data.id;
          for (const pn of picked) {
            const { id, ...rest } = pn;
            await axios.post(`${mon}/panels/tabs/${enc(tabId)}/panels`, rest);
          }
          log(t('monitoring:wiz.run.dashMade', { name: dashName.trim(), n: picked.length }));
        } catch (err) { log(`${t('monitoring:wiz.run.dashFailed')}: ${formatApiError(err, t)}`, false); }
      }
    } catch (err) {
      log(formatApiError(err, t), false);
    } finally {
      setRunning(false);
      setDoneOk(anyOk);
      if (anyOk && onDone) onDone();
    }
  };

  // ── 공유(시작 뒤) ──
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 2) { setFound([]); return undefined; }
    timer.current = setTimeout(async () => {
      try { setFound(((await axios.get(`${mon}/users/search`, { params: { q: q.trim() } })).data.data || []).filter(u => !viewers.some(v => v.usrid === u.usrid))); } catch (_e) { setFound([]); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, mon, viewers]);
  const saveShare = async () => {
    try {
      await axios.put(`${mon}/instances/${encodeURIComponent(target.instanceId)}/sharing`, { viewers: viewers.map(v => v.usrid) });
      setRunLog(l => [...l, { text: t('monitoring:wiz.run.shared', { n: viewers.length }), ok: true }]);
    } catch (err) { setRunLog(l => [...l, { text: formatApiError(err, t), ok: false }]); }
  };

  // ── 그리기 ──
  const options = useMemo(() => instances.flatMap(inst => (inst.conns || []).filter(c => c.canRegister).map(c => ({
    instanceId: inst.instanceId, connId: c.id, connName: c.name, shared: c.shared,
    takenByOther: inst.registered && !inst.canManage, mine: inst.registered && inst.canManage,
    // 등록 기록만으로 「수집 중」 이라 하지 않는다 — 실제로 도는 묶음 수(collecting)로 가른다(2026-10-28 신고)
    collecting: Number(inst.collecting) || 0, lastError: inst.lastError || null,
    owner: inst.owner,
  }))), [instances]);

  const body = (() => {
    switch (STEPS[step]) {
      case 'target':
        return (
          <>
            <p className="wiz-lead">{t('monitoring:wiz.target.lead')}</p>
            {options.length === 0 && <div className="wiz-empty">{t('monitoring:wiz.target.none')}</div>}
            <div className="wiz-list">
              {options.map(o => (
                <button key={o.connId} type="button" disabled={o.takenByOther}
                  className={`wiz-item ${target && target.connId === o.connId ? 'active' : ''}`}
                  onClick={() => { setTarget(o); setPre(null); }}>
                  <b>{o.connName}</b> <span className="wiz-mono">{o.instanceId}</span>
                  {o.shared && <span className="wiz-badge">{t('monitoring:cstat.sharedConn')}</span>}
                  {o.mine && o.collecting > 0 && <span className="wiz-badge ok">{t('monitoring:wiz.target.mineAlready')}</span>}
                  {o.mine && o.collecting === 0 && (
                    <span className="wiz-badge warn" title={o.lastError ? t('monitoring:wiz.target.lastError', { error: o.lastError.error }) : undefined}>
                      {t('monitoring:wiz.target.mineStopped')}
                    </span>
                  )}
                  {o.takenByOther && <span className="wiz-badge warn">{t('monitoring:wiz.target.takenByOther', { name: (o.owner && (o.owner.name || o.owner.usrid)) || t('monitoring:cstat.ownerAdmin') })}</span>}
                </button>
              ))}
            </div>
          </>
        );
      case 'precheck':
        return (
          <>
            <p className="wiz-lead">{t('monitoring:wiz.pre.lead')}</p>
            {preBusy && <div className="wiz-muted"><FiLoader className="wiz-spin" /> {t('monitoring:wiz.pre.running')}</div>}
            {preError && <div className="wiz-error">{preError}</div>}
            {pre && (
              <>
                {pre.alreadyOwned && <div className="wiz-error">{t('monitoring:wiz.pre.alreadyOwned')}</div>}
                <table className="wiz-table">
                  <thead><tr><th>{t('monitoring:wiz.pre.col.check')}</th><th>{t('monitoring:wiz.pre.col.groups')}</th><th>{t('monitoring:wiz.pre.col.result')}</th></tr></thead>
                  <tbody>
                    {pre.checks.map(c => (
                      <tr key={c.id}>
                        <td className="wiz-mono">{t(`monitoring:wiz.pre.check.${c.id}`)}</td>
                        <td>{(c.groups || []).map(groupLabel).join(', ')}</td>
                        <td>{c.ok ? <span className="wiz-ok"><FiCheck size={12} /> OK</span> : <span className="wiz-fail" title={c.code}><FiX size={12} /> {/^ORA-|^ERROR$/.test(c.code) ? c.code : t(`common:errors.${c.code}`, { defaultValue: c.code })}</span>}</td>
                      </tr>
                    ))}
                    {dbType === 'oracle' && <tr><td>{t('monitoring:wiz.pre.rac')}</td><td /><td>{pre.nodes == null ? '-' : t('monitoring:wiz.pre.nodes', { n: pre.nodes })}</td></tr>}
                    <tr><td>{dbType === 'postgresql' ? t('monitoring:wiz.pre.packPg') : t('monitoring:wiz.pre.pack')}</td><td /><td className="wiz-mono">{pre.pack || (dbType === 'postgresql' ? t('monitoring:wiz.pre.packPgMissing') : '-')}</td></tr>
                    <tr><td>{t('monitoring:wiz.pre.version')}</td><td /><td className="wiz-mono">{pre.version || '-'}</td></tr>
                  </tbody>
                </table>
                {pre.checks.some(c => !c.ok) && (
                  <div className="wiz-warn"><FiAlertTriangle size={12} /> {t('monitoring:wiz.pre.someFailed')}
                    {aiAssist && <button type="button" className="app-btn wiz-ai" onClick={() => askAi('grants')} disabled={!!aiBusy}><FiZap size={12} /> {t('monitoring:wiz.ai.grants')}</button>}
                  </div>
                )}
                {aiNote.grants && <pre className="wiz-ai-note">{aiNote.grants}</pre>}
              </>
            )}
          </>
        );
      case 'groups':
        return (
          <>
            <p className="wiz-lead">{t('monitoring:wiz.groups.lead')}</p>
            {aiAssist && (
              <div className="wiz-ai-row">
                <input className="wiz-input" placeholder={t('monitoring:wiz.ai.goalPlaceholder')} value={goal} onChange={(e) => setGoal(e.target.value)} />
                <button type="button" className="app-btn wiz-ai" onClick={() => askAi('groups')} disabled={!!aiBusy || !goal.trim()}><FiZap size={12} /> {t('monitoring:wiz.ai.recommend')}</button>
              </div>
            )}
            {aiNote.groups && <div className="wiz-ai-note">{aiNote.groups}</div>}
            <div className="wiz-checks">
              {builtinGroups.map(g => (
                <label key={g} className={failedGroups.has(g) ? 'disabled' : ''} title={failedGroups.has(g) ? t('monitoring:wiz.groups.blocked') : ''}>
                  <input type="checkbox" disabled={failedGroups.has(g)} checked={groups.includes(g)}
                    onChange={(e) => setGroups(gs => (e.target.checked ? [...gs, g] : gs.filter(x => x !== g)))} />
                  <b>{groupLabel(g)}</b> <span className="wiz-muted">{dbType === 'postgresql' && ['sessions', 'wait'].includes(g) ? t(`monitoring:wiz.groupDescPg.${g}`) : t(`monitoring:wiz.groupDesc.${g}`)}</span>
                </label>
              ))}
            </div>
            <div className="wiz-row">
              <label>{t('monitoring:wiz.groups.interval')}
                <select className="wiz-input" value={intervalSec} onChange={(e) => setIntervalSec(Number(e.target.value))}>
                  {INTERVALS.map(n => <option key={n} value={n}>{t('monitoring:cstat.everySec', { n })}</option>)}
                </select>
              </label>
              <label>{t('monitoring:wiz.groups.retain')}
                <select className="wiz-input" value={retainDays} onChange={(e) => setRetainDays(Number(e.target.value))}>
                  {RETAINS.map(n => <option key={n} value={n}>{t('monitoring:wiz.groups.days', { n })}</option>)}
                </select>
              </label>
            </div>
            <div className="wiz-muted">{t('monitoring:wiz.groups.loadNote', { n: groups.filter(g => !failedGroups.has(g)).length + (customOn ? 1 : 0), sec: intervalSec })}</div>
          </>
        );
      case 'custom':
        return (
          <>
            <label className="wiz-toggle"><input type="checkbox" checked={customOn} onChange={(e) => setCustomOn(e.target.checked)} /> {t('monitoring:wiz.custom.toggle')}</label>
            {customOn && (
              <>
                <p className="wiz-lead">{t('monitoring:wiz.custom.lead')}</p>
                <input className="wiz-input" placeholder={t('monitoring:wiz.custom.name')} value={custom.name} onChange={(e) => setCustom(c => ({ ...c, name: e.target.value }))} maxLength={60} />
                {aiAssist && (
                  <div className="wiz-ai-row">
                    <input className="wiz-input" placeholder={t('monitoring:wiz.ai.sqlPlaceholder')} value={goal} onChange={(e) => setGoal(e.target.value)} />
                    <button type="button" className="app-btn wiz-ai" onClick={() => askAi('sql')} disabled={!!aiBusy || !goal.trim()}><FiZap size={12} /> {t('monitoring:wiz.ai.draftSql')}</button>
                  </div>
                )}
                {aiNote.sql && <div className="wiz-ai-note">{aiNote.sql}</div>}
                <textarea className="wiz-sql" rows={7} spellCheck={false} placeholder="SELECT ..." value={custom.sql}
                  onChange={(e) => { setCustom(c => ({ ...c, sql: e.target.value })); setTest(null); }} />
                <div className="wiz-row">
                  <button type="button" className="app-btn" onClick={runTest} disabled={testBusy || !custom.sql.trim()}><FiPlay size={12} /> {t('monitoring:wiz.custom.test')}</button>
                  <span className="wiz-muted">{t('monitoring:wiz.custom.testNote')}</span>
                </div>
                {test && test.error && <div className="wiz-error">{test.error}</div>}
                {test && test.columns && (
                  <>
                    <div className="wiz-muted">{t('monitoring:wiz.custom.testResult', { n: test.rows.length, ms: test.elapsedMs })}</div>
                    <table className="wiz-table">
                      <thead><tr>{test.columns.map(c => <th key={c.name}>{c.name}</th>)}</tr></thead>
                      <tbody>
                        <tr>{test.columns.map(c => (
                          <td key={c.name}>
                            <select className="wiz-input wiz-role" value={c.role} onChange={(e) => setRole(c.name, e.target.value)}>
                              <option value="value">{t('monitoring:wiz.custom.roleValue')}</option>
                              <option value="counter">{t('monitoring:wiz.custom.roleCounter')}</option>
                              <option value="label">{t('monitoring:wiz.custom.roleLabel')}</option>
                              <option value="skip">{t('monitoring:wiz.custom.roleSkip')}</option>
                            </select>
                          </td>
                        ))}</tr>
                        {test.rows.slice(0, 5).map((r, i) => <tr key={i}>{test.columns.map(c => <td key={c.name} className="wiz-mono">{String(r[c.name] ?? '')}</td>)}</tr>)}
                      </tbody>
                    </table>
                    {!testOk && <div className="wiz-warn"><FiAlertTriangle size={12} /> {t('monitoring:wiz.custom.needValue')}</div>}
                  </>
                )}
              </>
            )}
          </>
        );
      case 'dashboard':
        return (
          <>
            <p className="wiz-lead">{t('monitoring:wiz.dash.lead')}</p>
            <label className="wiz-toggle"><input type="checkbox" checked={makeDash} onChange={(e) => setMakeDash(e.target.checked)} /> {t('monitoring:wiz.dash.make')}</label>
            {makeDash && <input className="wiz-input" placeholder={t('monitoring:wiz.dash.name')} value={dashName} onChange={(e) => setDashName(e.target.value)} maxLength={50} />}
          </>
        );
      case 'confirm':
        return (
          <>
            <p className="wiz-lead">{t('monitoring:wiz.confirm.lead')}</p>
            <table className="wiz-table">
              <tbody>
                <tr><th>{t('monitoring:wiz.confirm.target')}</th><td>{target && `${target.connName} — ${target.instanceId}`}</td></tr>
                <tr><th>{t('monitoring:wiz.confirm.groups')}</th><td>{groups.filter(g => !failedGroups.has(g)).map(groupLabel).join(', ') || '-'}{customOn ? ` + ${custom.name}` : ''}</td></tr>
                <tr><th>{t('monitoring:wiz.confirm.schedule')}</th><td>{t('monitoring:wiz.confirm.scheduleText', { sec: intervalSec, days: retainDays })}</td></tr>
                <tr><th>{t('monitoring:wiz.confirm.dash')}</th><td>{makeDash ? dashName : t('monitoring:wiz.confirm.dashDefault')}</td></tr>
              </tbody>
            </table>
            <div className="wiz-muted">{t('monitoring:wiz.confirm.ownerNote')}</div>
            {runLog.length > 0 && (
              <ul className="wiz-log">{runLog.map((l, i) => <li key={i} className={l.ok ? 'ok' : 'fail'}>{l.ok ? <FiCheck size={12} /> : <FiX size={12} />} {l.text}</li>)}</ul>
            )}
          </>
        );
      case 'share':
        return (
          <>
            <p className="wiz-lead">{t('monitoring:wiz.share.lead')}</p>
            <div className="wiz-chips">
              {viewers.length === 0 && <span className="wiz-muted">{t('monitoring:cstat.noViewers')}</span>}
              {viewers.map(v => <span key={v.usrid} className="wiz-chip">{v.name}<button type="button" onClick={() => setViewers(vs => vs.filter(x => x.usrid !== v.usrid))}><FiX size={10} /></button></span>)}
            </div>
            <input className="wiz-input" placeholder={t('monitoring:cstat.searchPlaceholder')} value={q} onChange={(e) => setQ(e.target.value)} />
            {found.length > 0 && (
              <div className="wiz-list">
                {found.map(u => <button key={u.usrid} type="button" className="wiz-item" onClick={() => { setViewers(vs => [...vs, u]); setQ(''); }}><b>{u.name}</b> <span className="wiz-muted">{u.login}</span></button>)}
              </div>
            )}
            <div className="wiz-row"><button type="button" className="app-btn app-btn-primary" onClick={saveShare} disabled={viewers.length === 0}>{t('monitoring:cstat.saveShare')}</button></div>
            {runLog.length > 0 && <ul className="wiz-log">{runLog.map((l, i) => <li key={i} className={l.ok ? 'ok' : 'fail'}>{l.ok ? <FiCheck size={12} /> : <FiX size={12} />} {l.text}</li>)}</ul>}
          </>
        );
      default: return null;
    }
  })();

  const isConfirm = STEPS[step] === 'confirm';
  return (
    <AppModal windowed open={open} onClose={onClose} icon={<FiZap size={14} />} title={t('monitoring:wiz.title')} className="app-modal-default wiz-modal">
      <div className="wiz-frame">
        <ol className="wiz-steps">
          {STEPS.map((s, i) => (
            <li key={s} className={`${i === step ? 'now' : ''} ${i < step ? 'done' : ''}`}>
              <span className="wiz-step-no">{i < step ? <FiCheck size={11} /> : i + 1}</span>{t(`monitoring:wiz.step.${s}`)}
            </li>
          ))}
        </ol>
        <div className="wiz-body" key={step}>{body}</div>
      </div>
      <div className="wiz-footer">
        <button type="button" className="app-btn" onClick={onClose}>{doneOk ? t('common:ui.close') : t('monitoring:act.cancel')}</button>
        <span className="wiz-spacer" />
        {step > 0 && !doneOk && <button type="button" className="app-btn" onClick={() => go(-1)} disabled={running}><FiChevronLeft size={12} /> {t('monitoring:wiz.prev')}</button>}
        {isConfirm && !doneOk && (
          <button type="button" className="app-btn app-btn-primary" onClick={start} disabled={running}><FiPlay size={12} /> {t('monitoring:wiz.start')}</button>
        )}
        {isConfirm && doneOk && STEPS.includes('share') && <button type="button" className="app-btn app-btn-primary" onClick={() => setStep(step + 1)}>{t('monitoring:wiz.toShare')} <FiChevronRight size={12} /></button>}
        {!isConfirm && STEPS[step] !== 'share' && (
          <button type="button" className="app-btn app-btn-primary" onClick={() => go(1)} disabled={!canNext}>{t('monitoring:wiz.next')} <FiChevronRight size={12} /></button>
        )}
      </div>
    </AppModal>
  );
}
